<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useI18n } from 'vue-i18n';
import { OnClickOutside } from '@vueuse/components';

import TicketListHeader from './TicketListHeader.vue';
import TicketFilterModal from './TicketFilterModal.vue';
import TicketListItem from './TicketListItem.vue';
import TicketBulkActionsBar from './components/TicketBulkActionsBar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import TicketsAPI from 'dashboard/api/tickets.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useTicketBulkActions } from 'dashboard/composables/tickets/useTicketBulkActions.js';

const props = defineProps({
  ticketType: { type: String, default: 'all' },
  activeTicketId: { type: [String, Number], default: 0 },
});

const emit = defineEmits(['ticketSelect', 'create']);

const { t } = useI18n();

// State
const tickets = ref([]);
const isLoading = ref(false);
const currentPage = ref(1);
const totalPages = ref(1);
const ticketCount = ref(0);
const activeAssigneeTab = ref('me');
const statusFilter = ref('');
const priorityFilter = ref('');
const sortFilter = ref('created_at_desc');
const searchQuery = ref('');

const categoryFilter = ref('');
const subcategoryFilter = ref('');
const explotacionFilter = ref('');
const localidadFilter = ref('');
const coloniaFilter = ref('');
const showTicketFilters = ref(false);
const filterOptions = ref({
  categories: [],
  explotaciones: [],
  localidades: [],
  colonias: [],
});
const ticketFilterModalStyle = ref({});

const aquacisListWarning = ref('');
const aquacisCatalogWarning = ref('');

const currentUser = useMapGetter('getCurrentUser');
const teams = useMapGetter('teams/getTeams');

const { isCloudFeatureEnabled } = useAccount();
const isContractsEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.CONTRACTS)
);

// Team filter
const teamFilter = ref('');
const teamDropdownOpen = ref(false);
const teamSearch = ref('');

const teamFilterOptions = computed(() => [
  { id: '', name: t('TICKETS.FILTERS.ALL_TEAMS') },
  { id: 'none', name: t('TICKETS.FILTERS.NO_TEAM') },
  ...(teams.value || []),
]);

const filteredTeamOptions = computed(() => {
  const q = teamSearch.value.toLowerCase();
  if (!q) return teamFilterOptions.value;
  return teamFilterOptions.value.filter(o => o.name.toLowerCase().includes(q));
});

const currentTeamLabel = computed(
  () => teamFilterOptions.value.find(o => String(o.id) === String(teamFilter.value))?.name ?? t('TICKETS.FILTERS.ALL_TEAMS')
);

const selectTeamFilter = id => {
  teamFilter.value = id;
  teamDropdownOpen.value = false;
  teamSearch.value = '';
  fetchTickets();
};

const clearTeamFilter = () => {
  teamFilter.value = '';
  teamDropdownOpen.value = false;
  teamSearch.value = '';
  fetchTickets();
};

// Assignee tab items
const assigneeTabItems = computed(() => [
  { key: 'me', name: t('TICKETS.ASSIGNEE_TABS.ME') },
  { key: 'unassigned', name: t('TICKETS.ASSIGNEE_TABS.UNASSIGNED') },
  { key: 'all', name: t('TICKETS.ASSIGNEE_TABS.ALL') },
]);

const activeTabIndex = computed(() =>
  assigneeTabItems.value.findIndex(tab => tab.key === activeAssigneeTab.value)
);

// Title based on ticket type
const panelTitle = computed(() => {
  const titleMap = {
    all: t('TICKETS.HEADER.ALL_TICKETS'),
    mention: t('TICKETS.HEADER.MENTIONS'),
    unattended: t('TICKETS.HEADER.UNATTENDED'),
  };
  return titleMap[props.ticketType] || t('TICKETS.HEADER.ALL_TICKETS');
});

// Build fetch params
const buildParams = () => {
  const params = {
    page: currentPage.value,
    sort: sortFilter.value,
  };

  if (props.ticketType === 'mention') {
    params.mentioned = true;
  } else if (activeAssigneeTab.value === 'me') {
    params.assignee_id = currentUser.value?.id;
  } else if (activeAssigneeTab.value === 'unassigned') {
    params.assignee_id = 'unassigned';
  }

  if (statusFilter.value) params.status = statusFilter.value;
  if (priorityFilter.value) params.priority = priorityFilter.value;
  if (searchQuery.value) params.q = searchQuery.value;
  if (props.ticketType === 'unattended') params.unattended = true;
  if (activeAssigneeTab.value === 'all' && teamFilter.value) params.team_id = teamFilter.value;

  if (categoryFilter.value) params.ticket_category_id = categoryFilter.value;
  if (subcategoryFilter.value) params.ticket_subcategory_id = subcategoryFilter.value;
  if (explotacionFilter.value) params.explotacion = explotacionFilter.value;
  if (localidadFilter.value) params.localidad = localidadFilter.value;
  if (coloniaFilter.value) params.colonia = coloniaFilter.value;

  return params;
};

const hasAppliedFilters = computed(() =>
  Boolean(
    statusFilter.value ||
      priorityFilter.value ||
      sortFilter.value !== 'created_at_desc' ||
      categoryFilter.value ||
      subcategoryFilter.value ||
      explotacionFilter.value ||
      localidadFilter.value ||
      coloniaFilter.value
  )
);

const appliedFilterValues = computed(() => ({
  status: statusFilter.value,
  priority: priorityFilter.value,
  sort: sortFilter.value,
  ticket_category_id: categoryFilter.value,
  ticket_subcategory_id: subcategoryFilter.value,
  explotacion: explotacionFilter.value,
  localidad: localidadFilter.value,
  colonia: coloniaFilter.value,
}));

const loadFilterOptions = async () => {
  if (!isContractsEnabled.value) {
    aquacisCatalogWarning.value = '';
    aquacisListWarning.value = '';
    return;
  }
  try {
    const { data } = await TicketsAPI.getFilterOptions();
    filterOptions.value = {
      categories: data.categories || [],
      explotaciones: data.explotaciones || [],
      localidades: data.localidades || [],
      colonias: data.colonias || [],
    };
    if (data.aquacis_error) {
      aquacisCatalogWarning.value =
        data.aquacis_error_message || t('TICKETS.FILTERS.AQUACIS_UNAVAILABLE');
    } else {
      aquacisCatalogWarning.value = '';
    }
  } catch {
    aquacisCatalogWarning.value = t('TICKETS.FILTERS.AQUACIS_UNAVAILABLE');
  }
};

// Fetch tickets
const fetchTickets = async (resetPage = true) => {
  if (resetPage) currentPage.value = 1;
  isLoading.value = true;
  try {
    const params = buildParams();
    const response = await TicketsAPI.get(params);
    const data = response.data;

    if (resetPage) {
      tickets.value = data.payload || data.data || [];
    } else {
      tickets.value = [...tickets.value, ...(data.payload || data.data || [])];
    }

    const meta = data.meta || {};
    totalPages.value = meta.total_pages || 1;
    ticketCount.value = meta.total_count || meta.count || tickets.value.length;
    if (meta.aquacis_error) {
      aquacisListWarning.value =
        meta.aquacis_error_message || t('TICKETS.FILTERS.AQUACIS_UNAVAILABLE');
    } else {
      aquacisListWarning.value = '';
    }
  } catch {
    // Silently handle fetch errors
  } finally {
    isLoading.value = false;
  }
};

// Load more (pagination)
const loadMore = () => {
  if (currentPage.value < totalPages.value && !isLoading.value) {
    currentPage.value += 1;
    fetchTickets(false);
  }
};

const onApplyTicketFilters = filters => {
  statusFilter.value = filters.status ?? '';
  priorityFilter.value = filters.priority ?? '';
  sortFilter.value = filters.sort ?? 'created_at_desc';
  categoryFilter.value = filters.ticket_category_id ?? '';
  subcategoryFilter.value = filters.ticket_subcategory_id ?? '';
  explotacionFilter.value = filters.explotacion ?? '';
  localidadFilter.value = filters.localidad ?? '';
  coloniaFilter.value = filters.colonia ?? '';
  showTicketFilters.value = false;
  fetchTickets();
};

const closeTicketFiltersModal = () => {
  showTicketFilters.value = false;
};

const updateTicketFilterPosition = () => {
  const trigger = document.getElementById('toggleTicketFilterButton');
  if (!trigger) return;

  const triggerRect = trigger.getBoundingClientRect();
  const viewportPadding = 8;
  const desiredWidth = 750;
  const modalWidth = Math.min(desiredWidth, window.innerWidth - viewportPadding * 2);

  let left = triggerRect.right + 8;
  const maxLeft = window.innerWidth - modalWidth - viewportPadding;
  left = Math.max(viewportPadding, Math.min(left, maxLeft));

  const top = Math.max(viewportPadding, triggerRect.bottom + 8);

  ticketFilterModalStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
  };
};

const onToggleTicketFiltersModal = () => {
  if (showTicketFilters.value) {
    closeTicketFiltersModal();
    return;
  }
  updateTicketFilterPosition();
  showTicketFilters.value = true;
};

const resetTicketFilters = () => {
  statusFilter.value = '';
  priorityFilter.value = '';
  sortFilter.value = 'created_at_desc';
  categoryFilter.value = '';
  subcategoryFilter.value = '';
  explotacionFilter.value = '';
  localidadFilter.value = '';
  coloniaFilter.value = '';
  closeTicketFiltersModal();
  fetchTickets();
};

const onSearch = query => {
  searchQuery.value = query;
  fetchTickets();
};

const onTicketClick = ticket => {
  emit('ticketSelect', ticket.display_id);
};

// Scroll handler for infinite loading
const onScroll = event => {
  const { scrollTop, scrollHeight, clientHeight } = event.target;
  if (scrollHeight - (scrollTop + clientHeight) < 100) {
    loadMore();
  }
};

// Watch for context changes
watch(
  () => props.ticketType,
  () => fetchTickets()
);

defineExpose({ fetchTickets });

useEmitter('ticket_mentioned', () => {
  if (props.ticketType === 'mention') {
    fetchTickets();
  }
});

// Silent refresh: updates list without showing loading spinner
const silentRefresh = async () => {
  try {
    const params = buildParams();
    const response = await TicketsAPI.get(params);
    const data = response.data;
    tickets.value = data.payload || data.data || [];
    const meta = data.meta || {};
    totalPages.value = meta.total_pages || 1;
    ticketCount.value = meta.total_count || meta.count || tickets.value.length;
    if (meta.aquacis_error) {
      aquacisListWarning.value =
        meta.aquacis_error_message || t('TICKETS.FILTERS.AQUACIS_UNAVAILABLE');
    } else {
      aquacisListWarning.value = '';
    }
  } catch {
    // Silently ignore errors during background refresh
  }
};

useEmitter('ticket_updated', silentRefresh);

// Background polling every 30 seconds
let pollingInterval = null;
onMounted(() => {
  fetchTickets();
  loadFilterOptions();
  pollingInterval = setInterval(silentRefresh, 30000);
  window.addEventListener('resize', updateTicketFilterPosition);
});
onBeforeUnmount(() => {
  if (pollingInterval) clearInterval(pollingInterval);
  window.removeEventListener('resize', updateTicketFilterPosition);
});

watch(showTicketFilters, async isOpen => {
  if (!isOpen) return;
  await nextTick();
  updateTicketFilterPosition();
});

const {
  selectedTicketIds,
  selectTicket,
  deselectTicket,
  clearSelectedTickets,
  isTicketSelected,
  selectAllTickets,
  onAssignTeam,
  onAssignAgent,
} = useTicketBulkActions(fetchTickets);

const allTicketsSelected = computed(
  () =>
    tickets.value.length > 0 &&
    tickets.value.length === selectedTicketIds.value.length &&
    tickets.value.every(t => selectedTicketIds.value.includes(t.display_id))
);

const onAssigneeTabChange = tabIndex => {
  const tab = assigneeTabItems.value[tabIndex];
  if (tab) {
    activeAssigneeTab.value = tab.key;
    if (tab.key !== 'all') teamFilter.value = '';
    clearSelectedTickets();
    fetchTickets();
  }
};
</script>

<template>
  <div
    class="relative flex flex-col w-[340px] min-w-[340px] h-full border-r border-n-weak bg-n-background overflow-x-visible overflow-y-hidden"
  >
    <TicketListHeader
      :title="panelTitle"
      :ticket-count="ticketCount"
      :has-applied-filters="hasAppliedFilters"
      :is-list-loading="isLoading"
      @search="onSearch"
      @filters-modal="onToggleTicketFiltersModal"
      @reset-filters="resetTicketFilters"
      @create="emit('create')"
    />

    <TeleportWithDirection
      v-if="showTicketFilters"
      to="body"
    >
      <TicketFilterModal
        :filter-options="filterOptions"
        :initial-values="appliedFilterValues"
        :popup-style="ticketFilterModalStyle"
        @apply="onApplyTicketFilters"
        @close="closeTicketFiltersModal"
      />
    </TeleportWithDirection>

    <div
      v-if="isContractsEnabled && aquacisListWarning"
      class="mx-3 px-2 py-1.5 text-xs rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-950 dark:text-amber-100 border border-amber-200 dark:border-amber-800"
      role="status"
    >
      {{ aquacisListWarning }}
    </div>
    <div
      v-if="isContractsEnabled && aquacisCatalogWarning"
      class="mx-3 px-2 py-1.5 text-xs rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-950 dark:text-amber-100 border border-amber-200 dark:border-amber-800"
      role="status"
    >
      {{ aquacisCatalogWarning }}
    </div>
    <div v-if="ticketType !== 'mention'" class="flex border-b border-n-weak">
      <button
        v-for="(tab, index) in assigneeTabItems"
        :key="tab.key"
        class="flex-1 px-3 py-2 text-xs font-medium text-center transition-colors"
        :class="{
          'text-n-brand border-b-2 border-n-brand': index === activeTabIndex,
          'text-n-slate-11 hover:text-n-slate-12': index !== activeTabIndex,
        }"
        @click="onAssigneeTabChange(index)"
      >
        {{ tab.name }}
      </button>
    </div>

    <!-- Team filter bar (only in "Todos" tab) -->
    <div
      v-if="activeAssigneeTab === 'all'"
      class="flex items-center gap-2 px-3 py-2 border-b border-n-weak"
    >
      <OnClickOutside class="relative flex-1 min-w-0" @trigger="teamDropdownOpen = false">
        <button
          type="button"
          class="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-n-solid-2 border border-n-weak text-xs text-n-slate-12 hover:border-n-slate-6 transition-colors"
          @click="teamDropdownOpen = !teamDropdownOpen"
        >
          <span class="truncate">{{ currentTeamLabel }}</span>
          <span
            class="flex-shrink-0 text-n-slate-10"
            :class="teamDropdownOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          />
        </button>
        <div
          v-if="teamDropdownOpen"
          class="absolute z-50 left-0 right-0 mt-1 rounded-lg bg-n-solid-2 border border-n-weak shadow-lg"
        >
          <div class="p-2 border-b border-n-weak">
            <input
              v-model="teamSearch"
              type="text"
              :placeholder="$t('TICKETS.FILTERS.BY_TEAM')"
              class="w-full px-2 py-1.5 text-xs bg-n-solid-3 border border-n-weak rounded-md text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:border-n-brand"
              autofocus
            />
          </div>
          <ul class="max-h-48 overflow-y-auto py-1">
            <li
              v-for="option in filteredTeamOptions"
              :key="option.id"
              class="flex items-center gap-2 px-2.5 py-1.5 text-xs cursor-pointer hover:bg-n-alpha-2 transition-colors"
              :class="String(option.id) === String(teamFilter) ? 'text-n-brand' : 'text-n-slate-12'"
              @click="selectTeamFilter(option.id)"
            >
              <span
                class="i-lucide-check flex-shrink-0"
                :class="String(option.id) === String(teamFilter) ? 'opacity-100' : 'opacity-0'"
              />
              {{ option.name }}
            </li>
            <li
              v-if="filteredTeamOptions.length === 0"
              class="px-3 py-2 text-xs text-n-slate-10 text-center"
            >
              {{ $t('TICKETS.FILTERS.NO_RESULTS') }}
            </li>
          </ul>
        </div>
      </OnClickOutside>
      <button
        v-if="teamFilter"
        class="flex-shrink-0 text-xs text-n-slate-11 hover:text-n-slate-12 px-2 py-1.5 rounded-lg hover:bg-n-solid-2 transition-colors whitespace-nowrap"
        @click="clearTeamFilter"
      >
        {{ $t('TICKETS.FILTERS.CLEAR_FILTERS') }}
      </button>
    </div>

    <!-- Bulk actions bar -->
    <TicketBulkActionsBar
      v-if="selectedTicketIds.length > 0"
      :ticket-count="selectedTicketIds.length"
      :all-tickets-selected="allTicketsSelected"
      @assign-team="onAssignTeam"
      @assign-agent="onAssignAgent"
    />

    <!-- Ticket list -->
    <div
      v-if="!isLoading || tickets.length > 0"
      class="flex-1 overflow-auto"
      @scroll="onScroll"
    >
      <TicketListItem
        v-for="ticket in tickets"
        :key="ticket.id"
        :ticket="ticket"
        :is-active="String(ticket.display_id) === String(activeTicketId)"
        :selected="isTicketSelected(ticket.display_id)"
        @click="onTicketClick"
        @select-ticket="selectTicket"
        @deselect-ticket="deselectTicket"
      />
      <div
        v-if="isLoading && tickets.length > 0"
        class="flex items-center justify-center py-4"
      >
        <Spinner :size="20" />
      </div>
    </div>

    <!-- Loading state (initial) -->
    <div
      v-if="isLoading && tickets.length === 0"
      class="flex-1 flex items-center justify-center"
    >
      <Spinner :size="24" />
    </div>

    <!-- Empty state -->
    <div
      v-if="!isLoading && tickets.length === 0"
      class="flex-1 flex items-center justify-center text-sm text-n-slate-11"
    >
      {{ $t('TICKETS.LIST.EMPTY') }}
    </div>
  </div>
</template>
